import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWindowSize } from '../utils/useWindowSize';
import { getSessions, getCourseCompletions, submitCourseCompletion, getHealingRequests, createHealingRequest, getNotifications, markNotificationsRead, getPractitionerActivities, practitionerActivityCheckin } from '../utils/api';
import axios from 'axios';
 
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
 
const StudentDashboard = () => {
  const { user, logout } = useAuth();
  const { isMobile } = useWindowSize();
  const [sessions, setSessions] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [healingRequests, setHealingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [enrolling, setEnrolling] = useState(null);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [showHealingForm, setShowHealingForm] = useState(false);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [myPractActivities, setMyPractActivities] = useState([]);
 
  const [courseForm, setCourseForm] = useState({
    courseName: '', courseCategory: 'healing',
    completionDate: '', certificateNumber: '',
    conductedBy: '', conductedAt: ''
  });
 
  const [healingForm, setHealingForm] = useState({
    condition: '', description: '', urgency: 'normal',
    isForSelf: true, beneficiaryName: '', beneficiaryAge: '',
    beneficiaryRelationship: 'spouse', beneficiaryRelationshipOther: '',
    medicalReportsShared: false, medicalReportsNote: '',
    consentComplementary: false, consentLiability: false, consentTestimonial: false
  });
 
  const courseList = {
    healing: ['Basic Pranic Healing', 'Advanced Pranic Healing', 'Pranic Psychotherapy', 'Crystal Healing', 'Pranic Crystal Healing', 'Superbrain Yoga'],
    spiritual: ['Arhatic Yoga Preparatory', 'Achieving Oneness with Higher Soul', 'Kriyashakti', 'Om Mani Padme Hum', 'Meditations on the Soul', 'Higher Clairvoyance'],
    prosperity: ['Pranic Feng Shui', 'Manifestation and Abundance', 'Prosperity Meditation']
  };
 
  const statusColors = {
    submitted: { bg: '#eaf4fb', color: '#2980b9' },
    under_review: { bg: '#fef9e7', color: '#d4ac0d' },
    approved: { bg: '#eafaf1', color: '#27ae60' },
    rejected: { bg: '#fdf0f0', color: '#e74c3c' }
  };
 
  const healingStatusColors = {
    pending: { bg: '#fef9e7', color: '#d4ac0d' },
    assigned: { bg: '#eaf4fb', color: '#2980b9' },
    in_progress: { bg: '#eafaf1', color: '#27ae60' },
    completed: { bg: '#f0f0f0', color: '#666' }
  };
 
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(function() {
    var fetchNotifs = async function() {
      try {
        var res = await getNotifications();
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unreadCount || 0);
      } catch (err) {}
    };
    fetchNotifs();
    var interval = setInterval(fetchNotifs, 60000);
    return function() { clearInterval(interval); };
  }, []);

  const fetchData = async () => {
    try {
      const [sessionsRes, completionsRes, healingRes, practRes] = await Promise.all([
        getSessions(),
        getCourseCompletions(),
        getHealingRequests(),
        getPractitionerActivities()
      ]);
      setSessions(sessionsRes.data.sessions);
      setCompletions(completionsRes.data.completions);
      setHealingRequests(healingRes.data.requests);
      setMyPractActivities(practRes.data || []);
    } catch (error) {
      console.log('Error:', error);
    } finally {
      setLoading(false);
    }
  };
 
  const handleEnroll = async (sessionId, sessionTitle) => {
    if (!window.confirm('Enroll in "' + sessionTitle + '"?')) return;
    setEnrolling(sessionId);
    try {
      const token = localStorage.getItem('pwc_token');
      await axios.post(API_URL + '/sessions/' + sessionId + '/enroll', {}, { headers: { Authorization: 'Bearer ' + token } });
      alert('Enrolled successfully!');
      fetchData();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error enrolling');
    } finally {
      setEnrolling(null);
    }
  };
 
  const handleCourseSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitCourseCompletion(courseForm);
      alert('Course completion submitted for admin verification.');
      setShowCourseForm(false);
      setCourseForm({ courseName: '', courseCategory: 'healing', completionDate: '', certificateNumber: '', conductedBy: '', conductedAt: '' });
      fetchData();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error submitting course');
    }
  };
 
  const handleHealingSubmit = async (e) => {
    e.preventDefault();
    if (!healingForm.consentComplementary || !healingForm.consentLiability || !healingForm.consentTestimonial) {
      alert('Please agree to all three consent statements before submitting.');
      return;
    }
    try {
      await createHealingRequest({ ...healingForm, isForSelf: healingForm.isForSelf === true || healingForm.isForSelf === 'true' });
      alert('Healing request submitted. Admin will assign a healer shortly.');
      setShowHealingForm(false);
      setHealingForm({
        condition: '', description: '', urgency: 'normal',
        isForSelf: true, beneficiaryName: '', beneficiaryAge: '',
        beneficiaryRelationship: 'spouse', beneficiaryRelationshipOther: '',
        medicalReportsShared: false, medicalReportsNote: '',
        consentComplementary: false, consentLiability: false, consentTestimonial: false
      });
      fetchData();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error submitting request');
    }
  };
 
  const approvedCourses = completions.filter(function(c) { return c.status === 'approved'; });
  const pendingCourses = completions.filter(function(c) { return c.status === 'submitted' || c.status === 'under_review'; });
  const upcomingSessions = sessions.filter(function(s) { return s.status === 'upcoming'; });
 
  const whatsappReportUrl = 'https://wa.me/919176880807?text=Hello%2C%20I%20would%20like%20to%20share%20my%20medical%20reports.%20My%20name%20is%20' + encodeURIComponent((user && user.firstName) || '') + '.';
 
  var s = { ...styles,
    topbarInner: { ...styles.topbarInner, ...(isMobile ? { padding: '0 12px', height: 'auto', flexWrap: 'wrap', gap: '8px', paddingTop: '10px', paddingBottom: '10px' } : {}) },
    container: { ...styles.container, ...(isMobile ? { padding: '0 10px' } : {}) },
    tabs: { ...styles.tabs, ...(isMobile ? { overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' } : {}) },
    welcomeBanner: { ...styles.welcomeBanner, ...(isMobile ? { flexDirection: 'column', padding: '20px 16px', alignItems: 'stretch' } : {}) },
    welcomeStats: { ...styles.welcomeStats, ...(isMobile ? { flexWrap: 'wrap', gap: '12px', width: '100%', padding: '12px', justifyContent: 'center' } : {}) },
    welcomeStatDivider: { ...styles.welcomeStatDivider, ...(isMobile ? { display: 'none' } : {}) },
    homeGrid: { ...styles.homeGrid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) },
    linksGrid: { ...styles.linksGrid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) },
    formGrid: { ...styles.formGrid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) },
    formGrid3: { ...styles.formGrid3, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) },
    formBtns: { ...styles.formBtns, ...(isMobile ? { flexDirection: 'column' } : {}) },
    submitBtn: { ...styles.submitBtn, ...(isMobile ? { width: '100%' } : {}) },
    cancelBtn: { ...styles.cancelBtn, ...(isMobile ? { width: '100%' } : {}) },
    reportsRow: { ...styles.reportsRow, ...(isMobile ? { flexDirection: 'column' } : {}) },
    requestInfoRow: { ...styles.requestInfoRow, ...(isMobile ? { flexDirection: 'column' } : {}) }
  };

  const renderHealingForm = function() {
    return (
      <div style={s.formBox}>
        <div style={s.formTitle}>New Healing Request</div>
        <form onSubmit={handleHealingSubmit}>
          <div style={s.toggleRow}>
            <div style={s.toggleLabel}>Who is this request for?</div>
            <div style={s.toggleBtns}>
              <button type="button" onClick={function() { setHealingForm({ ...healingForm, isForSelf: true }); }} style={healingForm.isForSelf ? s.toggleBtnActive : s.toggleBtn}>Myself</button>
              <button type="button" onClick={function() { setHealingForm({ ...healingForm, isForSelf: false }); }} style={!healingForm.isForSelf ? s.toggleBtnActive : s.toggleBtn}>Someone Else</button>
            </div>
          </div>
 
          {!healingForm.isForSelf && (
            <div style={s.beneficiaryBox}>
              <div style={s.beneficiaryTitle}>Beneficiary Details</div>
              <div style={s.formGrid3}>
                <div style={s.field}>
                  <label style={s.label}>Patient Name</label>
                  <input style={s.input} value={healingForm.beneficiaryName} onChange={function(e) { setHealingForm({ ...healingForm, beneficiaryName: e.target.value }); }} placeholder="Full name" required={!healingForm.isForSelf} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Age</label>
                  <input style={s.input} type="number" value={healingForm.beneficiaryAge} onChange={function(e) { setHealingForm({ ...healingForm, beneficiaryAge: e.target.value }); }} placeholder="Age" required={!healingForm.isForSelf} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Relationship</label>
                  <select style={s.input} value={healingForm.beneficiaryRelationship} onChange={function(e) { setHealingForm({ ...healingForm, beneficiaryRelationship: e.target.value }); }}>
                    <option value="spouse">Spouse</option>
                    <option value="child">Child</option>
                    <option value="parent">Parent</option>
                    <option value="sibling">Sibling</option>
                    <option value="friend">Friend</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}
 
          <div style={s.formGrid}>
            <div style={s.field}>
              <label style={s.label}>Condition / Health Concern</label>
              <input style={s.input} value={healingForm.condition} onChange={function(e) { setHealingForm({ ...healingForm, condition: e.target.value }); }} placeholder="e.g. Diabetes, Back Pain, Stress" required />
            </div>
            <div style={s.field}>
              <label style={s.label}>Urgency</label>
              <select style={s.input} value={healingForm.urgency} onChange={function(e) { setHealingForm({ ...healingForm, urgency: e.target.value }); }}>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div style={{ ...s.field, gridColumn: '1 / -1' }}>
              <label style={s.label}>Detailed Description</label>
              <textarea style={{ ...s.input, height: '90px', resize: 'vertical' }} value={healingForm.description} onChange={function(e) { setHealingForm({ ...healingForm, description: e.target.value }); }} placeholder="Describe the condition in detail..." required />
            </div>
          </div>
 
          <div style={s.reportsBox}>
            <div style={s.reportsRow}>
              <div>
                <div style={s.reportsTitle}>Share Medical Reports via WhatsApp</div>
                <div style={s.reportsSub}>Send any medical reports or test results to help our healers.</div>
              </div>
              <a href={whatsappReportUrl} target="_blank" rel="noreferrer" style={s.whatsappBtn}>Share via WhatsApp</a>
            </div>
            <div style={s.checkRow}>
              <input type="checkbox" id="healingReportsShared" checked={healingForm.medicalReportsShared} onChange={function(e) { setHealingForm({ ...healingForm, medicalReportsShared: e.target.checked }); }} style={{ marginRight: '8px', accentColor: '#4a8f82' }} />
              <label htmlFor="healingReportsShared" style={{ fontSize: '13px', color: '#333', cursor: 'pointer' }}>I have shared / will share medical reports via WhatsApp</label>
            </div>
          </div>
 
          {/* Consent Section */}
          <div style={s.consentBox}>
            <div style={s.consentTitle}>Consent Declaration</div>
            <div style={s.consentSub}>Please read and agree to all three statements before submitting your request.</div>
 
            <div style={s.consentItem}>
              <input type="checkbox" id="sc1"
                checked={healingForm.consentComplementary}
                onChange={function(e) { setHealingForm({ ...healingForm, consentComplementary: e.target.checked }); }}
                style={{ marginRight: '12px', accentColor: '#4a8f82', flexShrink: 0, marginTop: '2px' }}
              />
              <label htmlFor="sc1" style={s.consentLabel}>
                I understand that Pranic Healing is not meant to replace conventional medicine but rather to complement it.
              </label>
            </div>
 
            <div style={s.consentItem}>
              <input type="checkbox" id="sc2"
                checked={healingForm.consentLiability}
                onChange={function(e) { setHealingForm({ ...healingForm, consentLiability: e.target.checked }); }}
                style={{ marginRight: '12px', accentColor: '#4a8f82', flexShrink: 0, marginTop: '2px' }}
              />
              <label htmlFor="sc2" style={s.consentLabel}>
                I hereby release the person or organisations providing Pranic Healing from any liabilities as a result of the service received by me.
              </label>
            </div>
 
            <div style={{ ...s.consentItem, borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }}>
              <input type="checkbox" id="sc3"
                checked={healingForm.consentTestimonial}
                onChange={function(e) { setHealingForm({ ...healingForm, consentTestimonial: e.target.checked }); }}
                style={{ marginRight: '12px', accentColor: '#4a8f82', flexShrink: 0, marginTop: '2px' }}
              />
              <label htmlFor="sc3" style={s.consentLabel}>
                I hereby give my consent to the healer to use this testimonial for his Pranic Healing Certification Program.
              </label>
            </div>
          </div>
 
          <div style={s.formBtns}>
            <button type="submit" style={s.submitBtn}>Submit Healing Request</button>
            <button type="button" onClick={function() { setShowHealingForm(false); }} style={s.cancelBtn}>Cancel</button>
          </div>
        </form>
      </div>
    );
  };
 
  return (
    <div style={s.layout}>
      <div style={s.topbar}>
        <div style={s.topbarInner}>
          <img src="https://pranicwellnesscentre.com/wp-content/uploads/2018/10/PH-Revised-Logo.png" alt="Pranic Wellness Centre" style={s.logo} />
          <div style={s.topbarRight}>
            <div style={s.userInfo}>
              <span style={s.userName}>Welcome, {user && user.firstName}</span>
              <span style={s.userRole}>Student Member</span>
            </div>
            <div style={{ position: 'relative' }}>
              <button onClick={function() {
                setShowNotifPanel(!showNotifPanel);
                if (!showNotifPanel && unreadCount > 0) {
                  markNotificationsRead().then(function() { setUnreadCount(0); setNotifications(function(prev) { return prev.map(function(n) { return Object.assign({}, n, { read: true }); }); }); }).catch(function() {});
                }
              }} style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '4px', color: 'rgba(255,255,255,0.6)', fontSize: '18px', display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: '18px' }}>🔔</span>
                {unreadCount > 0 && (
                  <span style={{ position: 'absolute', top: '-2px', right: '-4px', background: '#e74c3c', color: '#fff', borderRadius: '10px', padding: '1px 5px', fontSize: '9px', fontWeight: '700', minWidth: '14px', textAlign: 'center', lineHeight: '14px' }}>{unreadCount}</span>
                )}
              </button>
              {showNotifPanel && (
                <div style={{ position: 'absolute', top: '40px', right: 0, width: '300px', maxHeight: '360px', overflowY: 'auto', background: '#fff', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 1000, border: '1px solid #e0e0e0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #e0e0e0' }}>
                    <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666' }}>Notifications</span>
                    <button onClick={function() { setShowNotifPanel(false); }} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '16px' }}>✕</button>
                  </div>
                  <div>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '12px' }}>No notifications</div>
                    ) : notifications.slice(0, 20).map(function(n, i) {
                      return (
                        <div key={i} style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0', background: n.read ? '#fff' : '#f9faf7' }}>
                          <div style={{ fontSize: '12px', color: '#333', lineHeight: '1.5' }}>{n.message}</div>
                          <div style={{ fontSize: '10px', color: '#999', marginTop: '3px' }}>{new Date(n.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <button onClick={logout} style={s.logoutBtn}>Logout</button>
          </div>
        </div>
      </div>
 
      <div style={s.main}>
        <div style={s.container}>
 
          <div style={s.tabs}>
            {[
              { key: 'home', label: 'Dashboard' },
              { key: 'sessions', label: 'Sessions (' + upcomingSessions.length + ')' },
              { key: 'courses', label: 'My Courses (' + completions.length + ')' },
              { key: 'healing', label: 'Healing Requests (' + healingRequests.length + ')' },
              { key: 'links', label: 'Quick Links' },
              { key: 'my-activities', label: 'My Activities' + (myPractActivities.filter(function(a) { return a.isActive; }).length > 0 ? ' (' + myPractActivities.filter(function(a) { return a.isActive; }).length + ')' : '') }
            ].map(function(tab) {
              return (
                <button key={tab.key} onClick={function() { setActiveTab(tab.key); setShowCourseForm(false); setShowHealingForm(false); }}
                  style={activeTab === tab.key ? { ...s.tab, ...s.tabActive } : s.tab}>
                  {tab.label}
                </button>
              );
            })}
          </div>
 
          {loading ? (
            <div style={s.loading}>Loading your dashboard...</div>
          ) : (
            <div>
 
              {activeTab === 'home' && (
                <div>
                  <div style={s.welcomeBanner}>
                    <div style={s.welcomeLeft}>
                      <div style={s.welcomeGreeting}>Hello, {user && user.firstName}!</div>
                      <div style={s.welcomeText}>Welcome to your Student Dashboard. Track courses, enroll in sessions and manage your healing journey.</div>
                    </div>
                    <div style={s.welcomeStats}>
                      <div style={s.welcomeStat}>
                        <div style={s.welcomeStatNum}>{approvedCourses.length}</div>
                        <div style={s.welcomeStatLabel}>Courses Approved</div>
                      </div>
                      <div style={s.welcomeStatDivider}></div>
                      <div style={s.welcomeStat}>
                        <div style={{ ...s.welcomeStatNum, color: '#d4ac0d' }}>{pendingCourses.length}</div>
                        <div style={s.welcomeStatLabel}>Pending</div>
                      </div>
                      <div style={s.welcomeStatDivider}></div>
                      <div style={s.welcomeStat}>
                        <div style={{ ...s.welcomeStatNum, color: '#2980b9' }}>{upcomingSessions.length}</div>
                        <div style={s.welcomeStatLabel}>Sessions</div>
                      </div>
                      <div style={s.welcomeStatDivider}></div>
                      <div style={s.welcomeStat}>
                        <div style={{ ...s.welcomeStatNum, color: '#27ae60' }}>{healingRequests.length}</div>
                        <div style={s.welcomeStatLabel}>Healing Requests</div>
                      </div>
                    </div>
                  </div>
 
                  <div style={s.homeGrid}>
                    <div style={s.card}>
                      <div style={s.cardHeader}>
                        <div>
                          <div style={s.cardTitle}>Upcoming Sessions</div>
                          <div style={s.cardSub}>Sessions you are eligible to attend</div>
                        </div>
                        <button onClick={function() { setActiveTab('sessions'); }} style={s.cardLink}>View All</button>
                      </div>
                      <div style={s.cardBody}>
                        {upcomingSessions.length === 0 ? (
                          <div style={s.empty}>
                            <div style={s.emptyTitle}>No sessions yet</div>
                            <div style={s.emptySub}>Submit your courses to unlock sessions.</div>
                          </div>
                        ) : upcomingSessions.slice(0, 3).map(function(session) {
                          return (
                            <div key={session._id} style={s.sessionItem}>
                              <div style={s.sessionDateBox}>
                                <span style={s.sessionDay}>{new Date(session.date).getDate()}</span>
                                <span style={s.sessionMonth}>{new Date(session.date).toLocaleString('en-IN', { month: 'short' })}</span>
                              </div>
                              <div style={s.sessionContent}>
                                <div style={s.sessionTitle}>{session.title}</div>
                                <div style={s.sessionMeta}>{session.startTime} — {session.venue}</div>
                              </div>
                              <button onClick={function() { handleEnroll(session._id, session.title); }} style={s.enrollBtn} disabled={enrolling === session._id}>
                                {enrolling === session._id ? '...' : 'Enroll'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
 
                    <div style={s.card}>
                      <div style={s.cardHeader}>
                        <div>
                          <div style={s.cardTitle}>Course Status</div>
                          <div style={s.cardSub}>Your submitted courses</div>
                        </div>
                        <button onClick={function() { setActiveTab('courses'); }} style={s.cardLink}>View All</button>
                      </div>
                      <div style={s.cardBody}>
                        {completions.length === 0 ? (
                          <div style={s.empty}>
                            <div style={s.emptyTitle}>No courses submitted</div>
                            <button onClick={function() { setActiveTab('courses'); setShowCourseForm(true); }} style={s.emptyBtn}>Submit Course</button>
                          </div>
                        ) : completions.slice(0, 4).map(function(comp) {
                          return (
                            <div key={comp._id} style={s.courseItem}>
                              <div>
                                <div style={s.courseItemTitle}>{comp.courseName}</div>
                                <div style={s.courseItemSub}>{comp.courseCategory}</div>
                              </div>
                              <span style={{ ...s.badge, background: statusColors[comp.status] ? statusColors[comp.status].bg : '#f0f0f0', color: statusColors[comp.status] ? statusColors[comp.status].color : '#666' }}>
                                {comp.status ? comp.status.replace('_', ' ') : ''}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
 
                    <div style={s.card}>
                      <div style={s.cardHeader}>
                        <div>
                          <div style={s.cardTitle}>Healing Requests</div>
                          <div style={s.cardSub}>Your healing journey</div>
                        </div>
                        <button onClick={function() { setActiveTab('healing'); setShowHealingForm(true); }} style={s.addBtn}>+ New</button>
                      </div>
                      <div style={s.cardBody}>
                        {healingRequests.length === 0 ? (
                          <div style={s.empty}>
                            <div style={s.emptyTitle}>No healing requests</div>
                            <div style={s.emptySub}>As a student you can also request healing.</div>
                            <button onClick={function() { setActiveTab('healing'); setShowHealingForm(true); }} style={s.emptyBtn}>Request Healing</button>
                          </div>
                        ) : healingRequests.slice(0, 3).map(function(req) {
                          return (
                            <div key={req._id} style={s.courseItem}>
                              <div>
                                <div style={s.courseItemTitle}>{req.condition}</div>
                                <div style={s.courseItemSub}>{req.assignedHealer ? req.assignedHealer.firstName + ' ' + req.assignedHealer.lastName : 'Pending assignment'}</div>
                              </div>
                              <span style={{ ...s.badge, background: healingStatusColors[req.status] ? healingStatusColors[req.status].bg : '#f0f0f0', color: healingStatusColors[req.status] ? healingStatusColors[req.status].color : '#666' }}>
                                {req.status ? req.status.replace('_', ' ') : ''}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
 
                  <div style={{ ...s.card, marginTop: '20px' }}>
                    <div style={s.cardHeader}><div style={s.cardTitle}>Quick Actions</div></div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '1px', background: '#f0f0f0' }}>
                      {[
                        { label: 'Submit Course', desc: 'Add a completed course for verification', action: function() { setActiveTab('courses'); setShowCourseForm(true); }, color: '#4a8f82' },
                        { label: 'Request Healing', desc: 'Submit a healing request for yourself or others', action: function() { setActiveTab('healing'); setShowHealingForm(true); }, color: '#5a9a6e' },
                        { label: 'View Sessions', desc: 'See all sessions you can attend', action: function() { setActiveTab('sessions'); }, color: '#2980b9' },
                        { label: 'Share Medical Reports', desc: 'Send reports via WhatsApp to healers', action: function() { window.open(whatsappReportUrl, '_blank'); }, color: '#25D366' }
                      ].map(function(action) {
                        return (
                          <button key={action.label} onClick={action.action} style={s.quickActionGrid}>
                            <div style={{ ...s.quickActionDot, background: action.color, width: '12px', height: '12px', marginBottom: '8px' }}></div>
                            <div style={s.quickActionLabel}>{action.label}</div>
                            <div style={s.quickActionDesc}>{action.desc}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
 
              {activeTab === 'sessions' && (
                <div style={s.card}>
                  <div style={s.cardHeader}>
                    <div>
                      <div style={s.cardTitle}>Available Sessions</div>
                      <div style={s.cardSub}>Sessions you are eligible to attend</div>
                    </div>
                  </div>
                  <div style={s.cardBody}>
                    {sessions.length === 0 ? (
                      <div style={s.empty}>
                        <div style={s.emptyTitle}>No sessions available</div>
                        <div style={s.emptySub}>Submit courses to unlock eligible sessions.</div>
                      </div>
                    ) : sessions.map(function(session) {
                      return (
                        <div key={session._id} style={s.sessionItemFull}>
                          <div style={s.sessionDateBox}>
                            <span style={s.sessionDay}>{new Date(session.date).getDate()}</span>
                            <span style={s.sessionMonth}>{new Date(session.date).toLocaleString('en-IN', { month: 'short' })}</span>
                          </div>
                          <div style={s.sessionContent}>
                            <div style={s.sessionTitle}>{session.title}</div>
                            <div style={s.sessionMeta}>{session.startTime} — {session.endTime} | {session.venue}</div>
                            <div style={s.sessionMeta}>{session.currentEnrollments}/{session.maxParticipants} enrolled | {session.type ? session.type.replace(/_/g, ' ') : ''}</div>
                          </div>
                          {session.currentEnrollments >= session.maxParticipants ? (
                            <span style={{ ...s.badge, background: '#fdf0f0', color: '#e74c3c' }}>Full</span>
                          ) : (
                            <button onClick={function() { handleEnroll(session._id, session.title); }} style={s.enrollBtn} disabled={enrolling === session._id}>
                              {enrolling === session._id ? 'Enrolling...' : 'Enroll'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
 
              {activeTab === 'courses' && (
                <div style={s.card}>
                  <div style={s.cardHeader}>
                    <div>
                      <div style={s.cardTitle}>My Course Completions</div>
                      <div style={s.cardSub}>Submit completed courses for admin verification</div>
                    </div>
                    <button onClick={function() { setShowCourseForm(!showCourseForm); }} style={s.addBtn}>{showCourseForm ? 'Cancel' : '+ Submit Course'}</button>
                  </div>
                  {showCourseForm && (
                    <div style={s.formBox}>
                      <form onSubmit={handleCourseSubmit}>
                        <div style={s.formGrid}>
                          <div style={s.field}>
                            <label style={s.label}>Category</label>
                            <select style={s.input} value={courseForm.courseCategory} onChange={function(e) { setCourseForm({ ...courseForm, courseCategory: e.target.value, courseName: '' }); }}>
                              <option value="healing">Healing</option>
                              <option value="spiritual">Spiritual</option>
                              <option value="prosperity">Prosperity</option>
                            </select>
                          </div>
                          <div style={s.field}>
                            <label style={s.label}>Course Name</label>
                            <select style={s.input} value={courseForm.courseName} onChange={function(e) { setCourseForm({ ...courseForm, courseName: e.target.value }); }} required>
                              <option value="">Select course...</option>
                              {courseList[courseForm.courseCategory].map(function(c) { return <option key={c} value={c}>{c}</option>; })}
                            </select>
                          </div>
                          <div style={s.field}>
                            <label style={s.label}>Completion Date</label>
                            <input style={s.input} type="date" value={courseForm.completionDate} onChange={function(e) { setCourseForm({ ...courseForm, completionDate: e.target.value }); }} required />
                          </div>
                          <div style={s.field}>
                            <label style={s.label}>Certificate Number (optional)</label>
                            <input style={s.input} value={courseForm.certificateNumber} onChange={function(e) { setCourseForm({ ...courseForm, certificateNumber: e.target.value }); }} placeholder="e.g. PH-2024-1234" />
                          </div>
                          <div style={s.field}>
                            <label style={s.label}>Conducted By</label>
                            <input style={s.input} value={courseForm.conductedBy} onChange={function(e) { setCourseForm({ ...courseForm, conductedBy: e.target.value }); }} placeholder="Instructor name" />
                          </div>
                          <div style={s.field}>
                            <label style={s.label}>Conducted At</label>
                            <input style={s.input} value={courseForm.conductedAt} onChange={function(e) { setCourseForm({ ...courseForm, conductedAt: e.target.value }); }} placeholder="City or centre name" />
                          </div>
                        </div>
                        <button type="submit" style={s.submitBtn}>Submit for Verification</button>
                      </form>
                    </div>
                  )}
                  <div style={s.cardBody}>
                    {completions.length === 0 ? (
                      <div style={s.empty}>
                        <div style={s.emptyTitle}>No courses submitted yet</div>
                        <div style={s.emptySub}>Click Submit Course to add your completed courses.</div>
                      </div>
                    ) : completions.map(function(comp) {
                      return (
                        <div key={comp._id} style={s.courseItem}>
                          <div>
                            <div style={s.courseItemTitle}>{comp.courseName}</div>
                            <div style={s.courseItemSub}>{comp.courseCategory} | {new Date(comp.submittedAt).toLocaleDateString('en-IN')}</div>
                            {comp.status === 'rejected' && comp.rejectionReason && <div style={{ fontSize: '12px', color: '#e74c3c', marginTop: '4px' }}>Reason: {comp.rejectionReason}</div>}
                          </div>
                          <span style={{ ...s.badge, background: statusColors[comp.status] ? statusColors[comp.status].bg : '#f0f0f0', color: statusColors[comp.status] ? statusColors[comp.status].color : '#666' }}>
                            {comp.status ? comp.status.replace('_', ' ') : ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
 
              {activeTab === 'healing' && (
                <div style={s.card}>
                  <div style={s.cardHeader}>
                    <div>
                      <div style={s.cardTitle}>Healing Requests</div>
                      <div style={s.cardSub}>As a student you can also request Pranic Healing</div>
                    </div>
                    <button onClick={function() { setShowHealingForm(!showHealingForm); }} style={s.addBtn}>{showHealingForm ? 'Cancel' : '+ New Request'}</button>
                  </div>
                  {showHealingForm && renderHealingForm()}
                  <div style={s.cardBody}>
                    {healingRequests.length === 0 ? (
                      <div style={s.empty}>
                        <div style={s.emptyTitle}>No healing requests yet</div>
                        <div style={s.emptySub}>Submit a healing request for yourself or on behalf of someone else.</div>
                      </div>
                    ) : healingRequests.map(function(req) {
                      return (
                        <div key={req._id} style={s.requestFull}>
                          <div style={s.requestFullHeader}>
                            <div>
                              <div style={s.requestCondition}>
                                {req.condition}
                                {!req.isForSelf && req.beneficiaryName && <span style={s.beneficiaryTag}>for {req.beneficiaryName}</span>}
                              </div>
                              <div style={s.requestMeta}>{new Date(req.requestedAt).toLocaleDateString('en-IN')} | Sessions: {req.totalSessionsCompleted}</div>
                            </div>
                            <span style={{ ...s.badge, background: healingStatusColors[req.status] ? healingStatusColors[req.status].bg : '#f0f0f0', color: healingStatusColors[req.status] ? healingStatusColors[req.status].color : '#666' }}>
                              {req.status ? req.status.replace('_', ' ') : ''}
                            </span>
                          </div>
                          <div style={s.requestDesc}>{req.description}</div>
                          <div style={s.requestInfoRow}>
                            <div style={s.requestInfoBox}>
                              <div style={s.requestInfoLabel}>Assigned Healer</div>
                              <div style={s.requestInfoValue}>{req.assignedHealer ? req.assignedHealer.firstName + ' ' + req.assignedHealer.lastName : 'Pending'}</div>
                            </div>
                            <div style={s.requestInfoBox}>
                              <div style={s.requestInfoLabel}>Sessions Done</div>
                              <div style={s.requestInfoValue}>{req.totalSessionsCompleted}</div>
                            </div>
                          </div>
                          <button onClick={function() { setExpandedRequest(expandedRequest === req._id ? null : req._id); }} style={s.expandBtn}>
                            {expandedRequest === req._id ? 'Hide History' : 'View Session History (' + (req.sessions ? req.sessions.length : 0) + ')'}
                          </button>
                          {expandedRequest === req._id && req.sessions && req.sessions.length > 0 && (
                            <div style={s.sessionHistory}>
                              {req.sessions.map(function(session, index) {
                                return (
                                  <div key={index} style={s.sessionHistoryItem}>
                                    <div style={s.sessionHistoryDate}>Session #{index + 1} — {new Date(session.date).toLocaleDateString('en-IN')}</div>
                                    <div style={s.sessionHistoryRemarks}>{session.healerRemarks || 'No remarks'}</div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
 
              {activeTab === 'links' && (
                <div style={s.linksGrid}>
                  {[
                    { title: 'Healing Courses', desc: 'View all certified Pranic Healing courses', url: 'https://pranicwellnesscentre.com/pranic-healing-courses-chennai/' },
                    { title: 'Spiritual Courses', desc: 'Explore Arhatic Yoga and spiritual development', url: 'https://pranicwellnesscentre.com/spiritual-courses-chennai/' },
                    { title: 'Twin Hearts Meditation', desc: 'Join our regular group meditation sessions', url: 'https://pranicwellnesscentre.com/twin-hearts-meditation-chennai/' },
                    { title: 'Upcoming Events', desc: 'View workshops and special programmes', url: 'https://pranicwellnesscentre.com/chennai-pranic-healing-programs/' },
                    { title: 'Share Medical Reports', desc: 'Send reports via WhatsApp', url: whatsappReportUrl },
                    { title: 'Contact Centre', desc: 'Get in touch with Pranic Wellness Centre', url: 'https://pranicwellnesscentre.com/chennai-pranic-healing-Contact/' }
                  ].map(function(link) {
                    return (
                      <a key={link.url} href={link.url} target="_blank" rel="noreferrer" style={s.linkCard}>
                        <div style={s.linkTitle}>{link.title}</div>
                        <div style={s.linkDesc}>{link.desc}</div>
                      </a>
                    );
                  })}
                </div>
              )}
 
            </div>
          )}

              {/* MY ACTIVITIES TAB */}
              {activeTab === 'my-activities' && (function() {
                var active = myPractActivities.filter(function(a) { return a.isActive; });
                var inactive = myPractActivities.filter(function(a) { return !a.isActive; });

                function getMyAssignee(act) {
                  return (act.assignees || [])[0] || null;
                }

                function getScheduledDates(act) {
                  var start = new Date(act.startDate);
                  var end = act.endDate ? new Date(act.endDate) : new Date();
                  var dates = [];
                  var cur = new Date(start);
                  while (cur <= end) {
                    var ds = cur.toISOString().split('T')[0];
                    var dow = cur.getDay();
                    var ok = act.frequency === 'daily' ? true : ((act.daysOfWeek || []).length === 0 || act.daysOfWeek.includes(dow));
                    if (ok) dates.push(ds);
                    cur.setDate(cur.getDate() + 1);
                  }
                  return dates;
                }

                function compliance(act) {
                  var assignee = getMyAssignee(act);
                  if (!act.trackCompliance || !assignee) return null;
                  var today = new Date().toISOString().split('T')[0];
                  var pastDates = getScheduledDates(act).filter(function(d) { return d < today; });
                  var total = pastDates.length * (act.timesPerDay || 1);
                  if (total === 0) return null;
                  var done = (assignee.checkIns || []).filter(function(c) { return pastDates.includes(c.date); }).length;
                  return Math.min(100, Math.round((done / total) * 100));
                }

                function isCheckedIn(act, slotIndex) {
                  var assignee = getMyAssignee(act);
                  if (!assignee) return false;
                  var today = new Date().toISOString().split('T')[0];
                  return (assignee.checkIns || []).some(function(c) { return c.date === today && c.slotIndex === slotIndex && !c.missed; });
                }

                function isMissedToday(act, slotIndex) {
                  var assignee = getMyAssignee(act);
                  if (!assignee) return false;
                  var today = new Date().toISOString().split('T')[0];
                  return (assignee.checkIns || []).some(function(c) { return c.date === today && c.slotIndex === slotIndex && c.missed; });
                }

                function handleCheckin(act, slotIndex, missed) {
                  var today = new Date().toISOString().split('T')[0];
                  practitionerActivityCheckin(act._id, { date: today, slotIndex: slotIndex, missed: missed || false }).then(function(res) {
                    setMyPractActivities(function(prev) {
                      return prev.map(function(a) {
                        if (a._id !== act._id) return a;
                        return Object.assign({}, a, { assignees: (a.assignees || []).map(function(x) { return Object.assign({}, x, { checkIns: res.data.checkIns }); }) });
                      });
                    });
                  }).catch(function() {});
                }

                function renderActivity(act) {
                  var comp = compliance(act);
                  var slots = act.slotLabels || ['Daily'];
                  return (
                    <div key={act._id} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '12px', overflow: 'hidden' }}>
                      <div style={{ padding: '14px 16px', borderBottom: '1px solid #f5f5f5' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' }}>{act.title}</div>
                            {act.description && <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{act.description}</div>}
                            <div style={{ fontSize: '11px', color: '#999' }}>{act.frequency === 'daily' ? 'Every day' : act.frequency === 'weekly' ? 'Weekly' : 'Custom days'} · {act.timesPerDay || 1}× {act.frequency === 'daily' ? 'per day' : 'per session'} · Starts {new Date(act.startDate).toLocaleDateString('en-IN')}{act.endDate ? ' → ' + new Date(act.endDate).toLocaleDateString('en-IN') : ''}</div>
                          </div>
                          {comp !== null && (
                            <div style={{ textAlign: 'center', flexShrink: 0, marginLeft: '12px' }}>
                              <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '20px', fontWeight: '700', color: comp >= 70 ? '#27ae60' : comp >= 40 ? '#d4ac0d' : '#e74c3c' }}>{comp}%</div>
                              <div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Compliance</div>
                            </div>
                          )}
                        </div>
                      </div>
                      {act.trackCompliance && act.isActive && (
                        <div style={{ padding: '12px 16px', background: '#fafafa', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Today:</span>
                          {slots.map(function(label, si) {
                            var done = isCheckedIn(act, si);
                            var missed = isMissedToday(act, si);
                            var locked = done || missed;
                            return (
                              <div key={si} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <button type="button" onClick={function() { if (!locked) handleCheckin(act, si, false); }}
                                  disabled={locked}
                                  style={{ padding: '6px 14px', borderRadius: '20px', border: '1px solid ' + (done ? '#27ae60' : '#ddd'), background: done ? '#eafaf1' : '#fff', color: done ? '#27ae60' : '#555', fontSize: '12px', fontWeight: '700', cursor: locked ? 'default' : 'pointer', opacity: missed ? 0.4 : 1 }}>
                                  {done ? '✓ ' : ''}{label}
                                </button>
                                {!done && (
                                  <button type="button" onClick={function() { if (!locked) handleCheckin(act, si, true); }}
                                    disabled={locked}
                                    style={{ padding: '6px 14px', borderRadius: '20px', border: '1px solid ' + (missed ? '#e74c3c' : '#e0e0e0'), background: missed ? '#fdf0f0' : '#fff8f8', color: missed ? '#e74c3c' : '#c0392b', fontSize: '12px', fontWeight: '700', cursor: locked ? 'default' : 'pointer' }}>
                                    {missed ? '✗ Missed' : 'Missed'}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div>
                    {myPractActivities.length === 0 ? (
                      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '40px', textAlign: 'center' }}>
                        <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginBottom: '8px' }}>No activities assigned</div>
                        <div style={{ fontSize: '12px', color: '#999' }}>Admin will assign practitioner activities here.</div>
                      </div>
                    ) : (
                      <div>
                        {active.length > 0 && (
                          <div style={{ marginBottom: '24px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#4a8f82', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px' }}>Active Activities ({active.length})</div>
                            {active.map(renderActivity)}
                          </div>
                        )}
                        {inactive.length > 0 && (
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px' }}>Past / Inactive ({inactive.length})</div>
                            {inactive.map(renderActivity)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
        </div>
      </div>
    </div>
  );
};

const styles = {
  layout: { minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif" },
  topbar: { background: 'linear-gradient(135deg, #071e12, #0c2617)', borderBottom: '3px solid #4a8f82' },
  topbarInner: { maxWidth: '1100px', margin: '0 auto', padding: '0 32px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { height: '40px' },
  topbarRight: { display: 'flex', alignItems: 'center', gap: '20px' },
  userInfo: { textAlign: 'right' },
  userName: { display: 'block', fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#fff' },
  userRole: { fontSize: '10px', color: '#8DC63F', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '700' },
  logoutBtn: { padding: '8px 20px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' },
  main: { padding: '32px 0' },
  container: { maxWidth: '1100px', margin: '0 auto', padding: '0 32px' },
  tabs: { display: 'flex', gap: '4px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px', marginBottom: '24px', width: 'fit-content' },
  tab: { padding: '10px 18px', background: 'transparent', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#666', cursor: 'pointer' },
  tabActive: { background: '#4a8f82', color: '#fff' },
  loading: { textAlign: 'center', padding: '60px', color: '#666', fontSize: '14px' },
  welcomeBanner: { background: 'linear-gradient(135deg, #071e12, #0c2617)', borderRadius: '8px', padding: '28px 32px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '24px' },
  welcomeLeft: { flex: 1 },
  welcomeGreeting: { fontFamily: "'Raleway', sans-serif", fontSize: '22px', fontWeight: '700', color: '#fff', marginBottom: '8px' },
  welcomeText: { fontSize: '13px', color: 'rgba(255,255,255,0.55)', lineHeight: '1.7', maxWidth: '500px' },
  welcomeStats: { display: 'flex', alignItems: 'center', flexShrink: 0, background: 'rgba(255,255,255,0.06)', borderRadius: '8px', padding: '16px 24px' },
  welcomeStat: { textAlign: 'center', padding: '0 16px' },
  welcomeStatNum: { fontFamily: "'Raleway', sans-serif", fontSize: '28px', fontWeight: '700', color: '#8DC63F', lineHeight: 1 },
  welcomeStatLabel: { fontSize: '10px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' },
  welcomeStatDivider: { width: '1px', height: '40px', background: 'rgba(255,255,255,0.1)' },
  homeGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' },
  card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' },
  cardHeader: { padding: '18px 24px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e' },
  cardSub: { fontSize: '12px', color: '#999', marginTop: '2px' },
  cardBody: { padding: '8px 0' },
  cardLink: { background: 'none', border: 'none', color: '#4a8f82', fontSize: '12px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer', textTransform: 'uppercase' },
  addBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '9px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  empty: { padding: '28px', textAlign: 'center' },
  emptyTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginBottom: '8px' },
  emptySub: { fontSize: '12px', color: '#999', marginBottom: '14px' },
  emptyBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer', textTransform: 'uppercase' },
  sessionItem: { display: 'grid', gridTemplateColumns: '48px 1fr auto', gap: '10px', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid #f5f5f5' },
  sessionItemFull: { display: 'grid', gridTemplateColumns: '64px 1fr auto', gap: '16px', alignItems: 'center', padding: '14px 24px', borderBottom: '1px solid #f5f5f5' },
  sessionDateBox: { background: 'linear-gradient(135deg, #071e12, #0c2617)', borderRadius: '6px', padding: '8px', textAlign: 'center' },
  sessionDay: { display: 'block', fontFamily: "'Raleway', sans-serif", fontSize: '18px', fontWeight: '700', color: '#8DC63F', lineHeight: 1 },
  sessionMonth: { fontSize: '9px', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '1px' },
  sessionContent: {},
  sessionTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '3px' },
  sessionMeta: { fontSize: '11px', color: '#666' },
  enrollBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '7px 14px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },
  courseItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', borderBottom: '1px solid #f5f5f5' },
  courseItemTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '2px' },
  courseItemSub: { fontSize: '11px', color: '#999' },
  quickActionGrid: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '20px', background: '#fff', border: 'none', cursor: 'pointer', textAlign: 'left' },
  quickActionDot: { borderRadius: '50%', flexShrink: 0 },
  quickActionLabel: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
  quickActionDesc: { fontSize: '11px', color: '#999', lineHeight: '1.5' },
  formBox: { padding: '24px', borderBottom: '1px solid #e0e0e0', background: '#f9faf7' },
  formTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '20px' },
  toggleRow: { marginBottom: '20px' },
  toggleLabel: { fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666', marginBottom: '8px' },
  toggleBtns: { display: 'flex', gap: '8px' },
  toggleBtn: { padding: '9px 24px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#666', cursor: 'pointer' },
  toggleBtnActive: { padding: '9px 24px', background: '#4a8f82', border: '1px solid #4a8f82', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#fff', cursor: 'pointer' },
  beneficiaryBox: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '16px', marginBottom: '20px' },
  beneficiaryTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#4a8f82', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' },
  formGrid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' },
  field: {},
  label: { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666', marginBottom: '6px', fontFamily: "'Raleway', sans-serif" },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '5px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333' },
  reportsBox: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '16px', marginBottom: '20px' },
  reportsRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', gap: '16px' },
  reportsTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
  reportsSub: { fontSize: '12px', color: '#666', lineHeight: '1.6' },
  whatsappBtn: { display: 'inline-block', background: '#25D366', color: '#fff', padding: '9px 18px', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', textDecoration: 'none', flexShrink: 0 },
  checkRow: { display: 'flex', alignItems: 'center' },
  consentBox: { background: '#fff', border: '2px solid #4a8f82', borderRadius: '8px', padding: '20px 24px', marginBottom: '20px' },
  consentTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginBottom: '6px' },
  consentSub: { fontSize: '12px', color: '#666', marginBottom: '16px', lineHeight: '1.5' },
  consentItem: { display: 'flex', alignItems: 'flex-start', marginBottom: '14px', paddingBottom: '14px', borderBottom: '1px solid #f0f0f0' },
  consentLabel: { fontSize: '13px', color: '#333', lineHeight: '1.6', cursor: 'pointer' },
  formBtns: { display: 'flex', gap: '12px', marginTop: '8px' },
  submitBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '12px 28px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  cancelBtn: { background: 'transparent', color: '#666', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '12px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  requestFull: { padding: '20px 24px', borderBottom: '1px solid #f0f0f0' },
  requestFullHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
  requestCondition: { fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
  beneficiaryTag: { marginLeft: '8px', fontSize: '11px', color: '#4a8f82', fontWeight: '600', fontStyle: 'italic' },
  requestDesc: { fontSize: '13px', color: '#666', lineHeight: '1.6', marginBottom: '12px' },
  requestMeta: { fontSize: '11px', color: '#999' },
  requestInfoRow: { display: 'flex', gap: '12px', marginBottom: '12px' },
  requestInfoBox: { background: '#f9faf7', borderRadius: '6px', padding: '10px 14px', flex: 1 },
  requestInfoLabel: { fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#999', marginBottom: '4px', fontFamily: "'Raleway', sans-serif" },
  requestInfoValue: { fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#1a1a2e' },
  expandBtn: { background: 'transparent', border: '1px solid #e0e0e0', color: '#4a8f82', borderRadius: '4px', padding: '7px 14px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '1px' },
  sessionHistory: { marginTop: '12px', background: '#f9faf7', borderRadius: '6px', padding: '16px' },
  sessionHistoryItem: { paddingBottom: '10px', marginBottom: '10px', borderBottom: '1px solid #e0e0e0' },
  sessionHistoryDate: { fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
  sessionHistoryRemarks: { fontSize: '13px', color: '#555', lineHeight: '1.6' },
  badge: { fontFamily: "'Raleway', sans-serif", fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', padding: '4px 10px', borderRadius: '20px', whiteSpace: 'nowrap' },
  linksGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' },
  linkCard: { display: 'block', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '24px', textDecoration: 'none' },
  linkTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '8px' },
  linkDesc: { fontSize: '13px', color: '#666', lineHeight: '1.6' }
};
 
export default StudentDashboard;